import React from 'react';
import { Box, IconButton, CircularProgress } from '@mui/material';
import { POWER_BUTTON_SIZE } from '../../styles/deviceStyles';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import TimerRing from './TimerRing';
import PowerRing from './PowerRing';
import TimerCountdown from './TimerCountdown';
import ReconnectingDots from './ReconnectingDots';
import { useTimerProgress } from '../../hooks/useTimerProgress';
import { TimerConfig, shouldShowDeviceTimer } from '../../utils/timerUtils';
import { POWER_RING_ACTIVE_PX, POWER_RING_IDLE_PX } from './CardStatusPowerChrome';

/**
 * Propiedades para el componente PowerButton
 */
export interface PowerButtonProps {
  isOn: boolean;
  isUnknown?: boolean;
  isLoading: boolean;
  isConfirming?: boolean;
  onClick: (deviceId: number) => void;
  deviceId: number;
  /** Diámetro del botón (default POWER_BUTTON_SIZE = 43). Escala anillo e ícono. */
  size?: number;
  // Nuevas props para timer
  timerConfig?: TimerConfig;
  isTimerActive?: boolean;
  timerStartTime?: number;
  timerPosition?: 'left' | 'right'; // Posición del timer: 'left' para DeviceModal, 'right' para DeviceCard
  websocketStatus?: 'connected' | 'disconnected' | 'connecting'; // Estado del WebSocket
  isDemo?: boolean; // Si el usuario es demo, deshabilitar controles
  onDemoRestriction?: () => void; // Callback para mostrar snackbar de restricción demo
}

/**
 * Botón de encendido/apagado para dispositivos
 * CÓDIGO EXACTO QUE FUNCIONA EN DeviceCard
 */
const PowerButton: React.FC<PowerButtonProps> = React.memo(({ 
  isOn, 
  isUnknown = false,
  isLoading, 
  isConfirming = false,
  onClick, 
  deviceId,
  size = POWER_BUTTON_SIZE,
  // Nuevas props para timer
  timerConfig,
  isTimerActive = false,
  timerStartTime,
  timerPosition = 'right', // Por defecto 'right' (DeviceCard)
  websocketStatus = 'connected', // Por defecto conectado
  isDemo = false,
  onDemoRestriction
}) => {
  const btnSize = size > 0 ? size : POWER_BUTTON_SIZE;
  const scale = btnSize / POWER_BUTTON_SIZE;
  // ON un poco más grueso que OFF; con border-box crece solo hacia adentro (mismo Ø).
  const ringBorderWidthOn = POWER_RING_ACTIVE_PX * scale;
  const ringBorderWidthOff = POWER_RING_IDLE_PX * scale;
  const iconFontSize = 20 * scale;
  // Halo del spinner: un poco mayor que el botón. Se centra con un wrapper flex
  // (NO usar transform en CircularProgress: MUI lo usa para girar y se pelea).
  const spinnerPad =
    btnSize === POWER_BUTTON_SIZE ? 8 : Math.max(4, Math.round(7 * scale));
  const spinnerSize = btnSize + spinnerPad;
  const spinnerThickness =
    btnSize === POWER_BUTTON_SIZE ? 3.5 : Math.max(1.5, Math.round(3.5 * scale * 2) / 2);
  // Si el estado es desconocido o undefined, mostrar como indeterminado (cargando)
  // isUnknown se pasa como true cuando está offline O cuando auth_required = true
  const showLoadingState = isLoading || isUnknown || isOn === undefined;
  
  // Verificar si el WebSocket no está conectado (mostrar animación de reconexión)
  const isWebSocketDisconnected = websocketStatus !== 'connected';
  
  // Debug: Log para verificar estado
  if (isWebSocketDisconnected) {
    console.log('[PowerButton] WebSocket desconectado, estado:', websocketStatus, 'deviceId:', deviceId);
  }
  
  // Solo se aplica el estado visual de encendido si explícitamente es ON y no es desconocido
  const buttonIsOn = isOn === true && !isUnknown && !isLoading;
  
  // Debug logs para diagnosticar (comentados temporalmente)
  console.log('🔍 PowerButton Debug:', {
  deviceId,
  timerConfig,
  isTimerActive,
  timerStartTime,
  buttonIsOn: buttonIsOn,
  oneTimeOnly: timerConfig?.oneTimeOnly
  });

  // Anillo/countdown: auto-off mientras ON; auto-on mientras OFF
  const shouldShowTimer = shouldShowDeviceTimer(isTimerActive, timerConfig, buttonIsOn);
  
  const timerProgress = useTimerProgress({
    timerConfig: timerConfig || {
      enabled: false,
      autoOnEnabled: false,
      autoOffEnabled: false,
      oneTimeOnly: false,
      autoOnHours: 0,
      autoOnMinutes: 0,
      autoOnSeconds: 0,
      autoOffHours: 0,
      autoOffMinutes: 0,
      autoOffSeconds: 0
    },
    isActive: shouldShowTimer,
    startTime: timerStartTime,
    deviceIsOn: buttonIsOn
  });

  // Cierre visual SOLO si el countdown terminó por tiempo (no al cancelar animación / cerrar modal)
  const prevCompletedRef = React.useRef(false);
  const timerJustFinishedToOffRef = React.useRef(false);
  const timerJustFinishedToOnRef = React.useRef(false);
  
  React.useEffect(() => {
    const justCompleted =
      timerProgress.completedNaturally && !prevCompletedRef.current;

    if (justCompleted) {
      if (timerConfig?.autoOffEnabled && buttonIsOn) {
        timerJustFinishedToOffRef.current = true;
        timerJustFinishedToOnRef.current = false;
      } else if (timerConfig?.autoOnEnabled && !buttonIsOn) {
        timerJustFinishedToOnRef.current = true;
        timerJustFinishedToOffRef.current = false;
      }
    } else if (timerProgress.isRunning) {
      // Nuevo ciclo de countdown: quitar forzado visual previo
      timerJustFinishedToOffRef.current = false;
      timerJustFinishedToOnRef.current = false;
    }
    // Si se cancela sin completar, NO forzar ON/OFF (no tocamos flags de un complete previo;
    // el efecto de buttonIsOn los limpia cuando llega el estado real).

    prevCompletedRef.current = timerProgress.completedNaturally;
  }, [
    timerProgress.completedNaturally,
    timerProgress.isRunning,
    timerConfig?.autoOffEnabled,
    timerConfig?.autoOnEnabled,
    buttonIsOn,
  ]);
  
  const renderedWithOffFlagRef = React.useRef(false);
  const renderedWithOnFlagRef = React.useRef(false);
  
  if (timerJustFinishedToOffRef.current && !renderedWithOffFlagRef.current) {
    renderedWithOffFlagRef.current = true;
  }
  if (timerJustFinishedToOnRef.current && !renderedWithOnFlagRef.current) {
    renderedWithOnFlagRef.current = true;
  }
  
  React.useEffect(() => {
    if (!buttonIsOn && renderedWithOffFlagRef.current && timerJustFinishedToOffRef.current) {
      timerJustFinishedToOffRef.current = false;
      renderedWithOffFlagRef.current = false;
    } else if (buttonIsOn) {
      renderedWithOffFlagRef.current = false;
    }

    if (buttonIsOn && renderedWithOnFlagRef.current && timerJustFinishedToOnRef.current) {
      timerJustFinishedToOnRef.current = false;
      renderedWithOnFlagRef.current = false;
    } else if (!buttonIsOn) {
      renderedWithOnFlagRef.current = false;
    }
  }, [buttonIsOn]);
  
  const effectiveButtonIsOn = isWebSocketDisconnected
    ? false
    : timerJustFinishedToOffRef.current
      ? false
      : timerJustFinishedToOnRef.current
        ? true
        : buttonIsOn;
  
  const visualButtonIsOn = effectiveButtonIsOn;
  
  // Interpolación de colores y grosor del anillo (como en Android)
  // Usar visualButtonIsOn para TODOS los aspectos visuales cuando el timer termina
  const ringBorderColor = visualButtonIsOn ? '#2391FF' : '#fff';
  // Enteros/medios: en size chico (22px) fracciones subpixel descentran el anillo.
  const ringBorderWidth = visualButtonIsOn
    ? Math.max(2, Math.round(ringBorderWidthOn))
    : Math.max(1.5, Math.round(ringBorderWidthOff * 2) / 2);

  return (
    <Box sx={{ position: 'relative', width: btnSize, height: btnSize, flexShrink: 0 }}>
      {/* Cronómetro: auto-off (ON) o auto-on (OFF). En DeviceCard va aparte (position=right). */}
      {timerPosition !== 'right' && (
        <TimerCountdown
          timeRemaining={timerProgress.timeRemaining}
          isActive={shouldShowTimer && timerProgress.isRunning}
          showIcon={true}
          size="small"
          position={timerPosition}
        />
      )}

      <Box
        sx={{
          position: 'relative',
          width: btnSize,
          height: btnSize,
          lineHeight: 0,
          flexShrink: 0,
        }}
      >
      {/* Spinner concéntrico: wrapper flex sobre el botón; el Progress no lleva transform. */}
      {showLoadingState && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: btnSize,
            height: btnSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 2,
            overflow: 'visible',
          }}
        >
          <CircularProgress
            size={spinnerSize}
            thickness={spinnerThickness}
            sx={{
              color: '#2391FF',
              flexShrink: 0,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
        </Box>
      )}
      
      {/* Botón */}
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          // Si el usuario es demo, mostrar snackbar y no hacer nada
          if (isDemo) {
            if (onDemoRestriction) {
              onDemoRestriction();
            }
            return;
          }
          
          if (!isLoading && !isUnknown && !isWebSocketDisconnected) {
            onClick(deviceId);
          }
        }}
        disabled={isDemo || showLoadingState || isWebSocketDisconnected}
        sx={{
          position: 'relative',
          width: btnSize,
          height: btnSize,
          minWidth: btnSize,
          minHeight: btnSize,
          maxWidth: btnSize,
          maxHeight: btnSize,
          borderRadius: '50%',
          backgroundColor: visualButtonIsOn ? '#FFFFFF' : '#383838',
          opacity: showLoadingState || isWebSocketDisconnected ? 0.5 : 1,
          '&:hover': {
            backgroundColor: visualButtonIsOn ? '#f5f5f5' : '#404040',
          },
          p: 0,
          m: 0,
          boxShadow: 'none',
          boxSizing: 'border-box',
          overflow: 'visible',
          transition: 'background-color 0.3s ease-in-out, opacity 0.3s ease-in-out',
        }}
      >
        {/* Mostrar animación de reconexión si WebSocket no está conectado */}
        {isWebSocketDisconnected ? (
          <ReconnectingDots size={Math.max(4, 6 * scale)} color="#2391FF" spacing={Math.max(2, 4 * scale)} />
        ) : (
          <>
            <PowerSettingsNewIcon
              sx={{
                fontSize: iconFontSize,
                color: visualButtonIsOn ? '#2391FF' : '#fff',
                display: 'block',
              }}
            />
            
            {/* Anillo SVG: auto-on = sentido actual (cw); auto-off = sentido contrario (ccw). */}
            {shouldShowTimer && timerProgress.isRunning ? (
              <TimerRing
                isActive
                progress={timerProgress.progress}
                size={btnSize}
                borderWidth={ringBorderWidth}
                color="#2391FF"
                direction={buttonIsOn ? 'ccw' : 'cw'}
              />
            ) : (
              <PowerRing
                size={btnSize}
                borderWidth={ringBorderWidth}
                color={ringBorderColor}
              />
            )}
          </>
        )}
        
      </IconButton>
      </Box>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Comparador personalizado para React.memo
  return (
    prevProps.isOn === nextProps.isOn &&
    prevProps.isUnknown === nextProps.isUnknown &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.isConfirming === nextProps.isConfirming &&
    prevProps.deviceId === nextProps.deviceId &&
    prevProps.size === nextProps.size &&
    prevProps.isTimerActive === nextProps.isTimerActive &&
    prevProps.timerStartTime === nextProps.timerStartTime &&
    prevProps.timerPosition === nextProps.timerPosition &&
    prevProps.websocketStatus === nextProps.websocketStatus && // CRÍTICO: Incluir websocketStatus
    prevProps.isDemo === nextProps.isDemo &&
    JSON.stringify(prevProps.timerConfig) === JSON.stringify(nextProps.timerConfig)
  );
});

export default PowerButton;
